<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-card>
        <v-toolbar dense dark :color="color">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn small color="add" @click="clickEditDefaultValueDialog({}, true)" :disabled="!(addCoFactorList && addCoFactorList.length)">
            <v-icon class="mr-1">add</v-icon>{{ $t('Button.Add') }}
          </v-btn>
        </v-toolbar>
        <v-card-text v-if="mapSameAnalyte.length">
          <v-alert v-model="mapSameAnalyte.length" type="warning" outline>
            <li v-for="(item, index) in mapSameAnalyte" :key="index">
              Cofactors <span v-for="(cofactor, index) in item" :key="index">{{ cofactor.CoFactorName }}
              <span v-if="index != item.length-1">, </span></span>are mapped to the same analyte {{ item[0].AnalyteName }}.
            </li>
            <div>{{ mapSameAnalyteText }}</div>
          </v-alert>
        </v-card-text>
        <v-data-table :headers="headers" :items="defaultValuesHasMapSameAnalyte" :loading="isLoading" :pagination.sync="pagination" item-key="key" :expand="expand">
          <template slot="items" slot-scope="props">
            <tr @click="props.expanded = !props.expanded" class="pointer" v-if="props.item.value.length" :class="[conflicting(props.item) ? 'error lighten-4' : 'amber lighten-4']">
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <span v-for="(i, index) in props.item.value" :key="i.Id">{{ i.CoFactorName }}
                      <span v-if="index + 1 != props.item.value.length">,&nbsp;&nbsp;</span></span>
                      <v-icon color="primary" dark small>info</v-icon>
                    </span>
                  </template>
                  <span>{{ linkAnalyteName(props.item.value) }}</span>
                </v-tooltip>
              </td>
              <td colspan="3">
                <span v-if="conflicting(props.item)">{{ $t('GuidelineAnalyteDefaultValues.hasConflictingMsg') }}</span>
              </td>
              <td class="text-xs-left">
                <v-btn small dark color="warning" @click.native="clickEditMapSameAnalyte(props.item)" @click.stop>
                  <v-icon class="mr-1">edit</v-icon>{{ $t('Button.Edit') }}
                </v-btn>
                <v-btn small>
                  <v-icon class="mr-1">expand_more</v-icon>expand
                </v-btn>
              </td>
            </tr>
            <tr v-else>
              <td>{{ props.item.value.CoFactorName }}</td>
              <td>{{ props.item.value.DefaultValue }}</td>
              <td>{{ props.item.value.Unit }}</td>
              <td>{{ props.item.value.Description }}</td>
              <td class="text-xs-left">
                <v-btn small dark color="warning" @click="clickEditDefaultValueDialog(props.item.value)">
                  <v-icon class="mr-1">edit</v-icon>{{ $t('Button.Edit') }}
                </v-btn>
                <v-btn v-if="canRemoveValues" small dark color="delete" @click="clickRemoveDefaultValueDialog(props.item.value)">
                  <v-icon class="mr-1">delete_forever</v-icon>{{ $t('Button.Remove') }}
                </v-btn>
              </td>
            </tr>
          </template>
          <template slot="expand" slot-scope="props" v-if="props.item.value.length">
            <v-data-table :headers="headers" :items="props.item.value" :data="props.item" hide-actions>
              <template slot="items" slot-scope="props">
                <tr :class="['grey lighten-4']">
                  <td>{{ props.item.CoFactorName }}</td>
                  <td>{{ props.item.DefaultValue }}</td>
                  <td>{{ props.item.Unit }}</td>
                  <td>{{ props.item.Description }}</td>
                  <td class="text-xs-left">
                    <v-btn v-if="props.item.conflicting" small @click="SetAsResolvedDefaultValue(props.item)" color="success">
                      <v-icon class="mr-1">done</v-icon>{{ $t('Button.SetAsResolvedDefaultValue') }}
                    </v-btn>
                    <v-btn v-if="canRemoveValues" small dark color="delete" @click="clickRemoveDefaultValueDialog(props.item)">
                      <v-icon class="mr-1">delete_forever</v-icon>{{ $t('Button.Remove') }}
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>

    <v-dialog v-model="editDialog" persistent max-width="800px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ editDialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="clearAndCloseEditForm">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear indeterminate v-if="isLoading"></v-progress-linear>
        <v-form v-model="editFormIsValid" ref="editForm" style="width: 100%">
          <v-card-text>
            <v-layout wrap pl-5 pr-5>
              <v-flex xs12>
                <v-autocomplete v-if="isAddingNewValue" label="CoFactor" v-bind:items="addCoFactorList" v-model="editDefaultValue.CoFactorId" item-text="Name" item-value="Id" :disabled="!isAddingNewValue">
                </v-autocomplete>
                <v-autocomplete v-if="!isAddingNewValue" label="CoFactor" v-bind:items="cofactorList" v-model="editDefaultValue.CoFactorId" item-text="Name" item-value="Id" :disabled="!isAddingNewValue">
                </v-autocomplete>
                <v-text-field label="DefaultValue" v-model="editDefaultValue.DefaultValue" :rules="formRuleValue">
                </v-text-field>
                <v-autocomplete label="Unit" v-bind:items="allUnitsShow" v-model="editDefaultValue.Unit" item-text="Name" item-value="Name"></v-autocomplete>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" :disabled="addSaveDisabled" @click="clickPutDefaultValue">
              <v-icon>done</v-icon>{{ editDialogAddOrSave }}
            </v-btn>
            <v-btn outline color="secondary" @click="clearAndCloseEditForm">{{ $t('Button.Cancel') }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editMapSameAnalyte" persistent max-width="800px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ editMapSameAnalyteDialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="clearAndCloseEditForm">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear indeterminate v-if="isLoading"></v-progress-linear>
        <v-form v-model="editFormIsValid" ref="editForm" style="width: 100%">
          <v-card-text>
            <v-layout wrap pl-5 pr-5>
              <v-flex xs12>
                <v-text-field label="CoFactors" v-model="editCofactors" disabled>
                </v-text-field>
                <v-text-field label="DefaultValue" v-model="editCofactorsDefaultValue" :rules="formRuleValue">
                </v-text-field>
                <v-autocomplete label="Unit" v-bind:items="allUnitsShow" v-model="editCofactorsUnit" item-text="Name" item-value="Name"></v-autocomplete>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" :disabled="!editCofactorsDefaultValue || !editCofactorsUnit" @click="putMapSameAnalyteDefaultValue">
              <v-icon>done</v-icon>{{ editDialogAddOrSave }}
            </v-btn>
            <v-btn outline color="secondary" @click="clearAndCloseEditForm">{{ $t('Button.Cancel') }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="removeDialog" persistent max-width="500px">
      <v-card>
        <v-toolbar dense color="primary" dark style="padding: 0px;">
          <v-toolbar-title>Delete CoFactor Value</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="clearAndCloseRemoveForm">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="headline">Are you sure you want to remove the default value for analyte:
          {{ removeDefaultValue.CoFactorName }}?</v-card-text>
        <v-progress-linear indeterminate v-if="isLoading"></v-progress-linear>
        <v-card-text>Warning: If removed, the default value will not be recoverable</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn dark color="delete" @click.native="clickDeleteDefaultValue">
            <v-icon>delete_forever</v-icon>{{ $t('Button.Remove') }}
          </v-btn>
          <v-btn color="secondary" @click.native="clearAndCloseRemoveForm" outline>{{ $t('Button.Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const guidelinesparameter = createNamespacedHelpers('guidelinesparameter')
  const cofactor = createNamespacedHelpers('cofactor')
  const authenticate = createNamespacedHelpers('authenticate')
  const unit = createNamespacedHelpers('unit')
  const analyte = createNamespacedHelpers('analyte')
  import FormValidationUtil from '@/components/mixins/FormValidationUtil'
  import {
    unitSelection,
  } from '@/helpers/unitSelection'

  export default {
    mixins: [FormValidationUtil],
    props: {
      title: {
        type: String,
        default: 'CoFactor Values',
      },
      color: {
        type: String,
        default: 'secondary',
      },
      loading: {
        type: Boolean,
        default: false,
      },
      canRemoveValues: {
        type: Boolean,
        default: true,
      },
      mapSameAnalyte: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        expand: true,
        isAddingNewValue: false,
        editDialog: false,
        editDefaultValue: {},
        editMapSameAnalyte: false,
        editMapSameAnalyteDefaultValue: [],
        editCofactors: '',
        editCofactorsDefaultValue: null,
        editCofactorsUnit: '',
        editFormIsValid: true,
        formRuleAnalyteName: [
          v => this.rules.required(v) || 'Analyte Name is required',
          v =>
          !this.isAddingNewValue ||
          !this.defaultValues
          .map(x => x.CoFactorName.toLowerCase())
          .includes(v.toLowerCase()) ||
          'A default value already exists for this analyte',
        ],
        formRuleUnits: [v => this.rules.required(v) || 'Unit is required'],
        formRuleValue: [
          v => this.rules.numeric(v) || 'DefaultValue must be a number',
        ],
        removeDialog: false,
        removeDefaultValue: {},
        headers: [{
          text: 'CoFactor',
          value: 'CoFactorName',
        },
        {
          text: 'DefaultValue',
          value: 'DefaultValue',
        },
        {
          text: 'Unit',
          value: 'Unit',
        },
        {
          text: 'Description',
          value: 'Description',
        },
        {
          text: 'Actions',
          value: '',
          align: 'center',
          width: '20%',
          sortable: false,
        },
        ],
        mapSameAnalyteText: 'The default values of the cofactors must be exactly the same. Otherwise, it can cause problems during guideline calculation.',
        pagination: {
          rowsPerPage: -1,
        },
      }
    },
    computed: {
      ...authenticate.mapGetters(['currentUserGroup']),
      ...guidelinesparameter.mapState(['guidelineDefaultValues', 'guidelineConfig']),
      ...cofactor.mapGetters(['cofactorList', 'strategyList', 'cofactorLinkAnalyte']),
      ...unit.mapGetters(['allUnits', 'allUnitGroups']),
      ...analyte.mapGetters(['tenantAnalyteList']),
      defaultValues() {
        if (this.strategyList.length) {
          let DataFetchStrategy = this.guidelineConfig.data.DataFetchStrategy
          let strategy = this.strategyList.find(x => x.StrategyCode == DataFetchStrategy)
          if (strategy) return strategy.CoFactorDefaultValues
          else return []
        }
      },
      defaultValuesHasMapSameAnalyte () {
        if (this.mapSameAnalyte) {
          let newDefaultValueWithSameAnalyte = JSON.parse(JSON.stringify(this.mapSameAnalyte))
          let newDefaultValue = this.defaultValues ? JSON.parse(JSON.stringify(this.defaultValues)) : []
          newDefaultValueWithSameAnalyte.forEach((x, xIndex) => {
            x.forEach((y, yIndex) => {
              let target = newDefaultValue.find(z => z.CoFactorId == y.CoFactorId)
              let targetIndex = newDefaultValue.findIndex(z => z.CoFactorId == y.CoFactorId)
              newDefaultValueWithSameAnalyte[xIndex].splice(yIndex, 1, target)
              if (targetIndex != -1) {
                newDefaultValue.splice(targetIndex, 1)
              }
            })
          })
          newDefaultValueWithSameAnalyte.forEach((x,index,arr) => {
            arr[index] = x.filter(Boolean)
          })
          newDefaultValueWithSameAnalyte.forEach((x,index,arr) => {
            if (x.length == 1) {
              arr[index] = x[0]
            }
          })
          newDefaultValueWithSameAnalyte = newDefaultValueWithSameAnalyte.filter(x => x.length != 0)
          if (newDefaultValue.length) {
            newDefaultValueWithSameAnalyte.push.apply(newDefaultValueWithSameAnalyte, newDefaultValue)
          }
          newDefaultValueWithSameAnalyte = newDefaultValueWithSameAnalyte.map((x, index) => ({key: index,value: x}))
          if (newDefaultValueWithSameAnalyte.length == 1 && newDefaultValueWithSameAnalyte[0].value.length == 0) {
            return this.defaultValues
          } else return newDefaultValueWithSameAnalyte
        } else return this.defaultValues
      },
      isLoading() {
        return this.loading && this.guidelineDefaultValues.isLoading
      },
      editDialogTitle() {
        return this.isAddingNewValue ?
          'Add New Default Value' :
          `Edit ${this.editDefaultValue.CoFactorName} Default Value`
      },
      editMapSameAnalyteDialogTitle() {
        return `Edit ${this.editCofactors} Default Value`
      },
      editDialogAddOrSave() {
        return this.isAddingNewValue ? 'add' : 'save'
      },
      allUnitsShow() {
        if (!this.allUnits.length) {
          this.getUnit()
        } else {
          if (this.editDefaultValue.CoFactorId) {
            let target = this.cofactorLinkAnalyte.find(x => x.CoFactorId == this.editDefaultValue.CoFactorId)
            return this.unitSelection(target, this.cofactorLinkAnalyte, this.tenantAnalyteList, this.allUnits, this.allUnitGroups, this.cofactorList, this.editDefaultValue.CoFactorId)
          } else if (this.editMapSameAnalyteDefaultValue.length) {
            let target = this.cofactorLinkAnalyte.find(x => x.CoFactorId == this.editMapSameAnalyteDefaultValue[0].CoFactorId)
            return this.unitSelection(target, this.cofactorLinkAnalyte, this.tenantAnalyteList, this.allUnits, this.allUnitGroups, this.cofactorList, this.editMapSameAnalyteDefaultValue[0].CoFactorId)
          }
        }
      },
      addCoFactorList() {
        if (this.strategyList.length) {
          let newCoFactorList = JSON.parse(JSON.stringify(this.cofactorList))
          let DataFetchStrategy = this.guidelineConfig.data.DataFetchStrategy
          let targetStrategy = this.strategyList.find(x => x.StrategyCode == DataFetchStrategy)
          if (this.cofactorList.length) {
            if (targetStrategy) {
              targetStrategy.CoFactorDefaultValues.forEach(x => {
                let target = newCoFactorList.findIndex(y => y.Id == x.CoFactorId)
                newCoFactorList.splice(target, 1)
              })
            }
            return newCoFactorList
          } else return []
        }
      },
      addSaveDisabled() {
        if (this.editDefaultValue.CoFactorId && this.editDefaultValue.DefaultValue && this.editDefaultValue.Unit) {
          return false
        } else return true
      },
    },
    methods: {
      ...cofactor.mapActions(['deleteCoFactorDefaultValue', 'addOrEditCoFactorDefaultValue', 'addMissingCoFactorDefaultValue']),
      ...unit.mapActions(['getUnitList', 'getUnitGroup']),
      unitSelection,
      getUnit() {
        this.getUnitList()
        this.getUnitGroup()
      },
      conflicting(item) {
        let defaultValues = []
        let units = []
        item.value.map(x => {
          defaultValues.push(x.DefaultValue)
          units.push(x.Unit)
        })
        defaultValues = Array.from(new Set(defaultValues))
        units = Array.from(new Set(units))
        if (defaultValues.length == 1 && units.length == 1) {
          return false
        } else {
          item.value.forEach(x => {
            x.conflicting = true
          })
          return true
        }
      },
      linkAnalyteName(item) {
        let analyte = ''
        this.mapSameAnalyte.forEach(x => {
          if (x.find(y => y.CoFactorId == item[0].CoFactorId))
            analyte = x.find(y => y.CoFactorId == item[0].CoFactorId).AnalyteName
        })
        return `Cofactors linked to analyte ${analyte}`
      },
      clickEditDefaultValueDialog(data, isAdd) {
        this.isAddingNewValue = isAdd || false
        this.editDefaultValue = JSON.parse(JSON.stringify(data))
        this.editDialog = true
      },
      clickEditMapSameAnalyte(data) {
        this.editMapSameAnalyteDefaultValue = JSON.parse(JSON.stringify(data.value))
        this.editMapSameAnalyte = true
        let str = ''
        this.editMapSameAnalyteDefaultValue.forEach(x => {
          str = str ? `${str},${x.CoFactorName}` : `${x.CoFactorName}`
        })
        this.editCofactorsDefaultValue = this.editMapSameAnalyteDefaultValue[0].DefaultValue
        this.editCofactorsUnit = this.editMapSameAnalyteDefaultValue[0].Unit
        this.editCofactors = str
      },
      clearAndCloseEditForm() {
        this.editDefaultValue = {}
        this.editDialog = false
        this.editMapSameAnalyte = false
        this.editMapSameAnalyteDefaultValue = []
        this.editCofactors = ''
        this.editCofactorsDefaultValue = null
        this.editCofactorsUnit = ''
        this.$refs['editForm'].reset()
      },
      clickRemoveDefaultValueDialog(data) {
        this.removeDefaultValue = JSON.parse(JSON.stringify(data))
        this.removeDialog = true
      },
      clearAndCloseRemoveForm() {
        this.removeDefaultValue = {}
        this.removeDialog = false
      },
      clickPutDefaultValue() {
        let that = this
        let targetData = {}
        // add
        if (this.addCoFactorList) {
          if (this.editDefaultValue && !this.editDefaultValue.CoFactorName && this.editDefaultValue.CoFactorId) {
            this.editDefaultValue.CoFactorName = this.cofactorList.find(x => x.Id == this.editDefaultValue.CoFactorId)
              .Name
          }
          let DataFetchStrategy = this.guidelineConfig.data.DataFetchStrategy
          let targetStrategy = this.strategyList.find(x => x.StrategyCode == DataFetchStrategy)
          targetData = {
            TenantId: this.currentUserGroup.id,
            StrategyId: targetStrategy.Id,
            CoFactorDefaultValues: {
              Id: null,
              CoFactorId: this.editDefaultValue.CoFactorId,
              CoFactorName: this.editDefaultValue.CoFactorName,
              DefaultValue: this.editDefaultValue.DefaultValue,
              Unit: this.editDefaultValue.Unit,
            },
          }
        }
        // edit
        else {
          targetData = {
            TenantId: this.currentUserGroup.id,
            StrategyId: this.editDefaultValue.StrategyId,
            CoFactorDefaultValues: {
              Id: this.editDefaultValue.Id,
              CoFactorId: this.editDefaultValue.CoFactorId,
              CoFactorName: this.editDefaultValue.CoFactorName,
              DefaultValue: this.editDefaultValue.DefaultValue,
              Unit: this.editDefaultValue.Unit,
            },
          }
        }
        let options = {
          data: targetData,
          successCb() {
            that.clearAndCloseEditForm()
          },
          failedCb: (err) => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err.response.data.Message + ' ' + err.response.data.ExceptionMessage,
              show: true,
            })
          },
        }
        this.addOrEditCoFactorDefaultValue(options)
      },
      putMapSameAnalyteDefaultValue() {
        let that = this
        let CoFactorDefaultValues = []
        this.editMapSameAnalyteDefaultValue.map(x => {
          let cofactor = {
            Id: x.Id,
            CoFactorId: x.CoFactorId,
            CoFactorName: x.CoFactorName,
            DefaultValue: this.editCofactorsDefaultValue,
            Unit: this.editCofactorsUnit,
            TenantId: this.currentUserGroup.id,
            StrategyId: x.StrategyId,
          }
          CoFactorDefaultValues.push(cofactor)
        })
        let options = {
          data: {
            CoFactorDefaultValues,
            TenantId: this.currentUserGroup.id,
            StrategyId: this.editMapSameAnalyteDefaultValue[0].StrategyId,
          },
          isMapSame: true,
          successCb() {
            that.clearAndCloseEditForm()
          },
          failedCb: (err) => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err.response.data.Message,
              show: true,
            })
          },
        }
        this.addMissingCoFactorDefaultValue(options)
      },
      SetAsResolvedDefaultValue(data) {
        let target = this.defaultValuesHasMapSameAnalyte.filter(x => {
          if (Array.isArray(x.value)) {
            if (x.value.find(y => y.CoFactorId == data.CoFactorId)) {
              return x.value.find(y => y.CoFactorId == data.CoFactorId)
            }
          }
        })[0].value
        this.editMapSameAnalyteDefaultValue = target
        this.editCofactorsDefaultValue = data.DefaultValue
        this.editCofactorsUnit = data.Unit
        this.putMapSameAnalyteDefaultValue()
      },
      clickDeleteDefaultValue() {
        let that = this
        let options = {
          Id: this.removeDefaultValue.Id,
          DataFetchStrategy: this.guidelineConfig.data.DataFetchStrategy,
          successCb() {
            that.clearAndCloseRemoveForm()
          },
          failedCb(msg) {
            console.error(msg)
            that.$emit('notify', {
              message: `failed to delete default value for ${that.removeDefaultValue.CoFactorName}: ${msg}`,
              type: 'error',
            })
          },
        }
        this.deleteCoFactorDefaultValue(options)
      },
    },
    mounted() {},
  }
</script>


<style scoped lang="stylus">
  .centerBtn {
    margin: 0 auto;
    display: block;
    width: 40%;
  }
</style>
